import * as React from 'react';
import { EventHandler, memo, SyntheticEvent, useCallback, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Collapse from '@mui/material/Collapse';
import { useField } from 'react-final-form';
import { FormControlLabel, Checkbox } from '@mui/material';

import Input from '../../Input';
import { MOBILE_MIN_WIDTH } from '../../utils';
import PhoneInput from '../../PhoneInput';
import { BaseButton } from '../../BaseComponents';
import SocialAuth from '../../SocialAuth';
import { defaultTheme, Provider } from '../../SocialAuth/theme';
import { useTheme } from '../../theme';
import { InputType, PassengerField } from '../../Passenger/types';
import { hideText, validate } from '../../Passenger/utils';
import MediaQuery from '../../MediaQuery/MediaQuery';
import FieldsOrder from './FieldsOrder/FieldsOrder';
import { useMediaQuery } from 'react-responsive';
import { AccountExistenceQuery, LoginType, useAccountExistenceLazyQuery } from '@websky/graphql';
import { CdnIcon, SafeHtml } from '@shared/ui';
import { formatPhoneNumberIntl } from 'react-phone-number-input';

export interface Props {
	/**
	 * Should display all fields
	 */
	open?: boolean;

	toggleOpen: () => void;
	isAuthorized?: boolean;
	submitForm: EventHandler<SyntheticEvent>;
	phone: PassengerField;
	email: PassengerField;
	loginType: LoginType;
	showSubscribeCheckbox?: boolean;
	isInputsReadonly?: boolean;
	variant?: 'outlined' | 'filled' | 'standard';
	autofocus?: boolean;
	onLoginOpen?: (preselectedLogin: string) => void;
}

let debounceTimeout = 0;

export enum UserExistStatus {
	Login = 'login',
	Register = 'register'
}

type UserExistInfo = {
	status: UserExistStatus;
	confirmedLoginTypes?: Set<LoginType>;
};

const Contacts = memo<Props>(props => {
	const theme = useTheme('Contacts').ContactsStyles;
	const [userExistInfo, setUserExistInfo] = useState<UserExistInfo | null>(null);

	const { t } = useTranslation('Contacts');
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const escapedT = useCallback(
		(label: string | void) =>
			typeof label !== 'undefined'
				? t(label, {
						// This is necessary to avoid colon problems in error messages.
						nsSeparator: '|',
						keySeparator: '>'
				  })
				: label,
		[]
	);

	const [
		checkAccountExistence,
		{ data: accountExistenceData, loading: isAccountExistenceLoading, variables: accountExistenceVars }
	] = useAccountExistenceLazyQuery();

	const updateUserExistenceState = (accountExistenceData: AccountExistenceQuery) => {
		const confirmedLoginTypes = new Set(userExistInfo?.confirmedLoginTypes || []);
		const paramsLoginType = accountExistenceVars?.params?.loginType;

		// Если аккаунт найден, добавляем текущее поле в сет подтвержденных
		if (accountExistenceData?.AccountExistence?.result) {
			confirmedLoginTypes.add(paramsLoginType);
			setUserExistInfo({ status: UserExistStatus.Login, confirmedLoginTypes });

			return;
		}

		// Если аккаунт не найден, но найден посредством другого поля, удаляем текущее поле из сета подтвержденных
		if (
			userExistInfo?.status === UserExistStatus.Login &&
			Array.from(userExistInfo?.confirmedLoginTypes || []).some(value => value !== paramsLoginType)
		) {
			confirmedLoginTypes.delete(paramsLoginType);
			setUserExistInfo({ status: UserExistStatus.Login, confirmedLoginTypes });

			return;
		}

		setUserExistInfo({ status: UserExistStatus.Register });
	};

	React.useEffect(() => {
		if (accountExistenceData) {
			updateUserExistenceState(accountExistenceData);
		}
	}, [accountExistenceData]);

	const phone = useField<string>(props.phone.name, {
		type: props.phone.input,
		initialValue: props.phone.defaultValue,
		validate: value => escapedT(validate(value, {}, InputType.Phone, props.phone.validations))
	});

	const email = useField<string>(props.email.name, {
		type: props.email.input,
		initialValue: props.email.defaultValue,
		validate: value => escapedT(validate(value, {}, InputType.Text, props.email.validations))
	});

	const onLoginOpenHandler = () => {
		// Если почта подтверждена, то берем ее, иначе другое подтвержденное поле
		const loginType = userExistInfo?.confirmedLoginTypes?.has(LoginType.Email)
			? LoginType.Email
			: Array.from(userExistInfo?.confirmedLoginTypes || [])[0] || props.loginType;
		props.onLoginOpen(
			loginType === LoginType.Email ? email.input.value : loginType === LoginType.Phone ? phone.input.value : null
		);
	};

	const subscribed = useField('subscribed', {
		type: 'checkbox',
		defaultValue: false
	});

	const onChangeHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement> | string, loginType: LoginType, isValid: boolean) => {
			if (!event) {
				return;
			}
			const value = typeof event === 'string' ? event : event.target.value;

			if (props.open && !props.isAuthorized && isValid) {
				clearTimeout(debounceTimeout);

				debounceTimeout = window.setTimeout(async () => {
					checkAccountExistence({
						variables: {
							params: {
								login: value,
								loginType
							}
						}
					});
				}, 500);
			}

			if (loginType === LoginType.Email) {
				email.input.onChange(event);
			}

			if (loginType === LoginType.Phone) {
				phone.input.onChange(event);
			}
		},
		[accountExistenceData, userExistInfo]
	);

	const userInfo: string[] = [];

	if (email.input.value) {
		userInfo.push(hideText(email.input.value));
	}

	if (phone.input.value) {
		userInfo.push(formatPhoneNumberIntl(`+${phone.input.value}`));
	}

	const inputVariant = props.variant ? props.variant : isMobile ? 'standard' : 'filled';
	const hasContactsError = email.meta.invalid || phone.meta.invalid;

	return (
		<div className={cn(theme.contacts, { [theme.short]: !props.open })}>
			<div className={theme.header}>
				<div className={theme.description__header}>
					<div className={theme.icon_notifications}>
						<CdnIcon icon="Info/Notifications" />
					</div>
					{t('Your contacts')}{' '}
					{!hasContactsError && <CdnIcon className={theme.icon_success} icon="Info/Item Icluded" />}
				</div>

				{!props.open && (
					<div className={theme.customer__info}>
						<div className={cn(theme.field)}>{userInfo.join(', ')}</div>
					</div>
				)}
			</div>

			{props.open && (
				<>
					<div className={theme.form__container}>
						<div className={theme.form}>
							<div className={theme.description}>
								<div className={theme.description__text}>
									<CdnIcon icon="Info/Warning" />

									<SafeHtml
										tag="div"
										className={theme.description__content}
										html={t(
											'Contact details are necessary for us to be able to pass on the latest flight information.'
										)}
									/>
								</div>
							</div>

							<div className={theme.fields}>
								<FieldsOrder reverse={props.loginType === LoginType.Email}>
									<div className={theme.field}>
										<PhoneInput
											disabled={props.isInputsReadonly}
											className={theme.field__contact}
											value={phone.input.value}
											textFieldsProps={{
												...phone.input,
												variant: inputVariant,
												disabled: props.isInputsReadonly,
												label: t('Phone'),
												type: 'tel',
												inputProps: { 'data-test-id': 'contacts-phone' },
												helperText:
													((phone.meta.modified && phone.meta.touched) ||
														phone.meta.submitFailed) &&
													(phone.meta.error || phone.meta.submitError),
												error:
													((phone.meta.modified && phone.meta.touched) ||
														phone.meta.submitFailed) &&
													(!!phone.meta.error || !!phone.meta.submitError),
												onChange: e =>
													onChangeHandler(
														(((e as unknown) as string) || '').replace(/\+/g, ''),
														LoginType.Phone,
														!validate(
															(e as unknown) as string,
															{},
															InputType.Phone,
															props.phone.validations
														)
													)
											}}
										/>
									</div>
									<div className={theme.field}>
										<Input
											variant={inputVariant}
											absoluteHintPosition={true}
											inputClassName={theme.field__contact}
											disabled={props.isInputsReadonly}
											autoFocus={props.loginType === LoginType.Email && props.autofocus}
											inputProps={{ 'data-test-id': 'contacts-email' }}
											TextFieldProps={{
												...email.input,
												value: email.input.value !== 'null' ? email.input.value : '',
												label: t('Email'),
												type: 'email',
												helperText:
													((email.meta.modified && email.meta.touched) ||
														email.meta.submitFailed) &&
													(email.meta.error || email.meta.submitError),
												error:
													((email.meta.modified && email.meta.touched) ||
														email.meta.submitFailed) &&
													(!!email.meta.error || !!email.meta.submitError),
												onChange: e =>
													onChangeHandler(
														e as React.ChangeEvent<HTMLInputElement>,
														LoginType.Email,
														!validate(
															e.target.value as string,
															{},
															InputType.Text,
															props.email.validations
														)
													)
											}}
										/>
									</div>
								</FieldsOrder>
							</div>
						</div>

						<Collapse
							mountOnEnter
							unmountOnExit
							in={
								!props.isAuthorized
									? !!userExistInfo && props.showSubscribeCheckbox
									: props.showSubscribeCheckbox
							}
						>
							<div className={theme.additional}>
								<FormControlLabel
									classes={{
										label: theme.checkbox__label
									}}
									label={
										<SafeHtml
											html={t('Receive a mailing list with promotional offers and discounts')}
										/>
									}
									control={
										<Checkbox
											color="primary"
											classes={{
												root: theme.checkbox,
												checked: theme.checkbox_checked
											}}
											checked={subscribed.input.checked}
											inputProps={subscribed.input as any}
										/>
									}
								/>
							</div>
						</Collapse>

						<Collapse mountOnEnter unmountOnExit in={!props.isAuthorized}>
							<div className={cn(theme.footer, theme.footer_login)}>
								<MediaQuery maxWidth="mobile">
									<CdnIcon className={theme.footer__timerIcon} icon="Info/Timer" />
								</MediaQuery>

								<div className={theme.footer__inner}>
									<div className={theme.footer__text}>
										<MediaQuery minWidth="mobile">
											{t(
												userExistInfo?.status === UserExistStatus.Login
													? 'Want to speed up passenger data entry? Log in and continue.'
													: 'Want to speed up passenger data entry? Sign up and continue.'
											)}
										</MediaQuery>

										<MediaQuery maxWidth="mobile">
											<span>{t('Want to speed up passenger data entry?')}</span>
										</MediaQuery>
									</div>
									<BaseButton
										variant="optionAction"
										onClick={onLoginOpenHandler}
										isLoading={isAccountExistenceLoading}
									>
										{t(userExistInfo?.status === UserExistStatus.Login ? 'Log in' : 'Sign up')}
									</BaseButton>
								</div>

								<MediaQuery minWidth="mobile">
									<div className={theme.footer__social}>
										<Provider
											value={{
												SocialAuth: {
													...defaultTheme.SocialAuth,
													button: theme.footer__social__button
												}
											}}
										>
											<SocialAuth />
										</Provider>
									</div>
								</MediaQuery>
							</div>
						</Collapse>
					</div>
				</>
			)}
		</div>
	);
});

export default Contacts;
